import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, getToken, setToken } from '@/lib/api';
import { setActiveUserId } from '@/lib/tradeJournal';
import type { User } from '@/lib/types';

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<User>;
  signUp: (name: string, email: string, password: string, opts?: { affiliateCode?: string; promoCode?: string }) => Promise<User>;
  affiliateSignUp: (name: string, email: string, password: string) => Promise<User>;
  signOut: () => void;
  refreshUser: () => Promise<User | null>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);

  // Propagate the user id to the trade journal module so all localStorage
  // reads/writes are scoped per-user. Prevents trade data from one TELOS
  // account leaking to another on the same browser.
  useEffect(() => {
    setActiveUserId(user?.id || null);
  }, [user?.id]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function bootstrap() {
      if (!getToken()) { setLoading(false); return; }
      try {
        const data = await api.get<{ user: User }>('/api/auth/me');
        if (!cancelled) setUser(data.user || null);
      } catch {
        setToken(null);
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    bootstrap();
    return () => { cancelled = true; };
  }, []);

  async function refreshFromServer(): Promise<User | null> {
    // Re-fetch /api/auth/me so the user object is the FULL shape with
    // whopMembershipId + promoCode. Login + signup responses omit those
    // fields and the paywall gates depend on them.
    try {
      const fresh = await api.get<{ user: User }>('/api/auth/me');
      setUser(fresh.user || null);
      return fresh.user || null;
    } catch {
      return null;
    }
  }

  async function signIn(email: string, password: string) {
    const data = await api.post<{ token: string; user: User }>('/api/auth/login', { email, password });
    setToken(data.token);
    setUser(data.user);
    const fresh = await refreshFromServer();
    return fresh || data.user;
  }

  async function signUp(name: string, email: string, password: string, opts: { affiliateCode?: string; promoCode?: string } = {}) {
    const data = await api.post<{ token: string; user: User }>('/api/auth/signup', { name, email, password, ...opts });
    setToken(data.token);
    setUser(data.user);
    const fresh = await refreshFromServer();
    return fresh || data.user;
  }

  // Partner-only sign-up — creates a role='affiliate' account (no dashboard).
  async function affiliateSignUp(name: string, email: string, password: string) {
    const data = await api.post<{ token: string; user: User }>('/api/auth/affiliate-signup', { name, email, password });
    setToken(data.token);
    setUser(data.user);
    const fresh = await refreshFromServer();
    return fresh || data.user;
  }

  function signOut() {
    setToken(null);
    setUser(null);
  }

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signUp, affiliateSignUp, signOut, refreshUser: refreshFromServer }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
